import { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import type { MapArea } from "../../content/map";

type Props = {
  area?: MapArea;
  isJa?: boolean;
};

/**
 * ユニット完了時の豪華セレブレーション演出
 * - 紙吹雪（Confetti）＆花火の Canvas アニメーション
 * - どうぶつ住民たちが左右からピョンピョン跳ねて拍手（クラップ）
 */
export function CelebrationVideo({ area, isJa = true }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let animId: number;
    let width = (canvas.width = window.innerWidth);
    let height = (canvas.height = window.innerHeight);

    const onResize = () => {
      width = canvas.width = window.innerWidth;
      height = canvas.height = window.innerHeight;
    };
    window.addEventListener("resize", onResize);

    // 紙吹雪ピース
    const colors = ["#ff7675", "#fdcb6e", "#e84393", "#00cec9", "#6c5ce7", "#ffeaa7", "#55efc4"];
    const pieces = Array.from({ length: 65 }, () => ({
      x: Math.random() * width,
      y: -20 - Math.random() * 100,
      vx: (Math.random() - 0.5) * 3,
      vy: 2.5 + Math.random() * 3.5,
      size: 8 + Math.random() * 8,
      color: colors[Math.floor(Math.random() * colors.length)],
      rot: Math.random() * 360,
      rotSpeed: (Math.random() - 0.5) * 8,
    }));

    const render = () => {
      ctx.clearRect(0, 0, width, height);

      for (const p of pieces) {
        p.x += p.vx;
        p.y += p.vy;
        p.rot += p.rotSpeed;
        if (p.y > height) {
          p.y = -20;
          p.x = Math.random() * width;
        }

        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate((p.rot * Math.PI) / 180);
        ctx.fillStyle = p.color;
        ctx.fillRect(-p.size / 2, -p.size / 4, p.size, p.size / 2);
        ctx.restore();
      }

      animId = requestAnimationFrame(render);
    };
    animId = requestAnimationFrame(render);

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener("resize", onResize);
    };
  }, []);

  const residentName = area ? (isJa ? area.residentNameJa : area.residentNameEn) : "住民";
  const residentEmoji = area?.residentEmoji ?? "🐾";

  return (
    <div className="absolute inset-0 pointer-events-none select-none overflow-hidden z-20">
      {/* 紙吹雪 Canvas */}
      <canvas ref={canvasRef} className="absolute inset-0 w-full h-full" />

      {/* 左側の祝福どうぶつ（うさぎ先生） */}
      <motion.div
        initial={{ x: -100, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        transition={{ delay: 0.2, type: "spring", damping: 12 }}
        className="absolute bottom-6 left-4 sm:left-12 flex flex-col items-center pointer-events-auto"
      >
        <motion.div
          animate={{
            y: [0, -16, 0],
            rotate: [-6, 6, -6],
          }}
          transition={{ repeat: Infinity, duration: 1.2, ease: "easeInOut" }}
          className="text-4xl sm:text-5xl drop-shadow-md cursor-pointer"
          whileHover={{ scale: 1.25 }}
        >
          🐰
        </motion.div>
        <div className="bg-[#fffdf7] border border-[#d9b45f] text-[#8f332c] text-[10px] sm:text-xs font-black px-2.5 py-1 rounded-full shadow-md mt-1">
          {isJa ? "うさぎ先生: すごい！" : "Sensei: Awesome!"}
        </div>
      </motion.div>

      {/* 右側の祝福どうぶつ（エリア住民） */}
      {area && (
        <motion.div
          initial={{ x: 100, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ delay: 0.35, type: "spring", damping: 12 }}
          className="absolute bottom-6 right-4 sm:right-12 flex flex-col items-center pointer-events-auto"
        >
          <motion.div
            animate={{
              y: [0, -18, 0],
              rotate: [6, -6, 6],
            }}
            transition={{ repeat: Infinity, duration: 1.1, ease: "easeInOut" }}
            className="text-4xl sm:text-5xl drop-shadow-md cursor-pointer"
            whileHover={{ scale: 1.25 }}
          >
            {residentEmoji}
          </motion.div>
          <div className="bg-[#fffdf7] border border-[#d9b45f] text-[#273f7a] text-[10px] sm:text-xs font-black px-2.5 py-1 rounded-full shadow-md mt-1">
            {residentName}: 拍手！👏
          </div>
        </motion.div>
      )}
    </div>
  );
}
